import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import classnames from 'classnames';

import type {
    InboxMessageFragment_creatorOrigin as CreatorOrigin,
    ThreadType,
} from 'nutshell-graphql-types';

import {Link} from '../../link/link';
import {Popover} from '../../popover/popover';
import {Body} from '../../typography/body';
import {useRefObserver} from '../../utils/hooks/use-ref-observer';

import {getFormattedTimestampForChannelEvent, getAvatar} from '../helpers';
import type {MessageSender} from '../types';

import {MessageBubble} from './message-bubble/message-bubble';
import type {Props as MessageBubbleProps} from './message-bubble/message-bubble';

import styles from './message.module.css';

type Props = MessageBubbleProps & {
    timestampToShow: any | null | undefined;
    sender: MessageSender | null | undefined;
    shouldShowAvatar: boolean;
    edition?: any;
    creatorOrigin?: CreatorOrigin | null | undefined;
    readTime?: number;
    isLastReadMessage?: boolean;
    images: string[] | null | undefined;
    submission?: any | null | undefined;
    shouldShowTimestampPopover: boolean;
    threadType?: ThreadType;
    isSidebar?: boolean;
};

export function Message(props: Props) {
    const {
        timestampToShow,
        sender,
        shouldShowAvatar,
        edition,
        creatorOrigin,
        ...messageBubbleProps
    } = props;

    const avatarRef = React.useRef(null);
    const {isHovered} = useRefObserver(avatarRef, ['isHovered']);

    const containerStylenames = classnames({
        [styles.container]: true,
        [styles['container--outbound']]: props.isOutbound,
        [styles['container--with-edition']]: Boolean(edition),
    });

    const showAvatar = shouldShowAvatar && (sender || creatorOrigin);
    let avatarTooltipText = null;
    if (sender) {
        avatarTooltipText = sender.name;
    } else if (creatorOrigin) {
        switch (creatorOrigin.originType) {
            case 'WHATSAPP':
                avatarTooltipText = 'Sent from your WhatsApp business account';
                break;
            case 'CHAT':
                avatarTooltipText = 'Automated message';
                break;
            case 'CHATBOT':
                avatarTooltipText = 'Sent by chatbot';
                break;
            case 'SMS_MARKETING':
                avatarTooltipText = 'Sent from your SMS marketing campaign';
                break;
            case 'SMS':
            case 'INSTAGRAM':
            default:
                avatarTooltipText = creatorOrigin.name;
        }
    }

    return (
        <>
            {timestampToShow && (
                <div className='flex justify-center mb-16'>
                    <Body size='small' color='light'>
                        {getFormattedTimestampForChannelEvent(timestampToShow)}
                    </Body>
                </div>
            )}
            <div className={containerStylenames}>
                {showAvatar ? (
                    <div className='align-self-start py-6' ref={avatarRef}>
                        {getAvatar(sender, creatorOrigin)}
                    </div>
                ) : (
                    <div className={styles['empty-avatar']} />
                )}
                <div className='flex flex-dir-col align-end gap-4 full-width'>
                    {edition && (
                        <Body size='small'>
                            Sent via{' '}
                            <Link
                                variant='primary'
                                size='small'
                                // @ts-expect-error
                                as={RoutedLink}
                                to={edition.campaign.htmlUrlPath}
                            >
                                {edition.campaign.name}
                            </Link>
                        </Body>
                    )}
                    <MessageBubble {...messageBubbleProps} />
                </div>
            </div>
            {isHovered && showAvatar && (
                <Popover
                    anchor={avatarRef.current}
                    location='left'
                    isNubDark={true}
                    bodyColor='dark'
                >
                    <div className='pad-8'>{avatarTooltipText}</div>
                </Popover>
            )}
        </>
    );
}
